import React, { useEffect, useState, useCallback } from 'react';
import './startupMessage.css';
import BASE_URL from "../../config";

// New admin tab: push text for the glasses' voice assistant to play on
// startup, and see release history. Mirrors the fetch/auth pattern used
// in pages/adminDashboard/adminDashboard.js (JWT from localStorage).
const StartupMessage = () => {
    const [text, setText] = useState('');
    const [messageType, setMessageType] = useState('announcement'); // 'announcement' | 'conversation'
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [pushing, setPushing] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null); // { type: 'ok' | 'err', text }
    const [togglingId, setTogglingId] = useState(null);

    // Inline edit state -- only one row can be in edit mode at a time.
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editType, setEditType] = useState('announcement');
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const authHeaders = () => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`,
    });

    const loadHistory = useCallback(() => {
        setLoadingHistory(true);
        fetch(`${BASE_URL}/api/announcements/history/?limit=25`, {
            headers: authHeaders(),
        })
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch history');
                return res.json();
            })
            .then((data) => {
                setHistory(data.data || []);
                setLoadingHistory(false);
            })
            .catch(() => {
                setLoadingHistory(false);
            });
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handlePush = () => {
        const trimmed = text.trim();
        if (!trimmed) {
            setStatusMsg({ type: 'err', text: 'Enter a message before pushing.' });
            return;
        }
        setPushing(true);
        setStatusMsg(null);

        fetch(`${BASE_URL}/api/announcements/push/`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ text: trimmed, message_type: messageType }),
        })
            .then((res) => {
                if (!res.ok) throw new Error('Push failed');
                return res.json();
            })
            .then(() => {
                setStatusMsg({ type: 'ok', text: 'Pushed. Startup playback is now on for all users.' });
                setText('');
                loadHistory();
            })
            .catch(() => {
                setStatusMsg({ type: 'err', text: 'Failed to push the message. Please try again.' });
            })
            .finally(() => {
                setPushing(false);
            });
    };

    const handleToggle = (id, nextEnabled) => {
        setTogglingId(id);
        fetch(`${BASE_URL}/api/announcements/${id}/toggle/`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ is_enabled: nextEnabled }),
        })
            .then((res) => {
                if (!res.ok) throw new Error('Toggle failed');
                return res.json();
            })
            .then(() => {
                loadHistory();
            })
            .catch(() => {
                setStatusMsg({ type: 'err', text: 'Failed to update that release. Please try again.' });
            })
            .finally(() => {
                setTogglingId(null);
            });
    };

    const startEdit = (m) => {
        setEditingId(m.id);
        setEditText(m.text);
        setEditType(m.message_type || 'announcement');
        setStatusMsg(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const saveEdit = (id) => {
        const trimmed = editText.trim();
        if (!trimmed) {
            setStatusMsg({ type: 'err', text: 'Message text cannot be empty.' });
            return;
        }
        setSavingEdit(true);
        fetch(`${BASE_URL}/api/announcements/${id}/edit/`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ text: trimmed, message_type: editType }),
        })
            .then((res) => {
                if (!res.ok) throw new Error('Edit failed');
                return res.json();
            })
            .then(() => {
                setStatusMsg({ type: 'ok', text: 'Release updated. This only corrects the record -- it does not re-send to devices.' });
                setEditingId(null);
                loadHistory();
            })
            .catch(() => {
                setStatusMsg({ type: 'err', text: 'Failed to save changes. Please try again.' });
            })
            .finally(() => {
                setSavingEdit(false);
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Permanently delete this release? This cannot be undone.')) {
            return;
        }
        setDeletingId(id);
        fetch(`${BASE_URL}/api/announcements/${id}/delete/`, {
            method: 'DELETE',
            headers: authHeaders(),
        })
            .then((res) => {
                if (!res.ok) throw new Error('Delete failed');
                return res.json();
            })
            .then(() => {
                setStatusMsg({ type: 'ok', text: 'Release deleted.' });
                if (editingId === id) setEditingId(null);
                loadHistory();
            })
            .catch(() => {
                setStatusMsg({ type: 'err', text: 'Failed to delete that release. Please try again.' });
            })
            .finally(() => {
                setDeletingId(null);
            });
    };

    const formatDate = (iso) => {
        try {
            return new Date(iso).toLocaleString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    return (
        <div className="aws-dashboard startup-message-page">
            <div className="aws-header">
                <div className="header-content">
                    <p className="aws-subtitle">OTA Service</p>
                    <h1 className="aws-title">Startup Message</h1>
                    <p className="aws-subtitle">
                        Write what the voice assistant should play the next time glasses start up.
                        Pushing new text makes it the active startup message for every user and turns
                        their startup playback setting on.
                    </p>
                </div>
            </div>

            <div className="sm-content">
                <div className="sm-panel">
                    <h2>Push new startup message</h2>
                    <p className="sm-hint">This becomes the message glasses play on next startup, for everyone.</p>
                    <div className="sm-type-row" role="radiogroup" aria-label="Delivery type">
                        <label className="sm-type-option">
                            <input
                                type="radio"
                                name="messageType"
                                value="announcement"
                                checked={messageType === 'announcement'}
                                onChange={() => setMessageType('announcement')}
                            />
                            <span>
                                <strong>Announcement</strong> — one-way, no reply expected
                            </span>
                        </label>
                        <label className="sm-type-option">
                            <input
                                type="radio"
                                name="messageType"
                                value="conversation"
                                checked={messageType === 'conversation'}
                                onChange={() => setMessageType('conversation')}
                            />
                            <span>
                                <strong>Conversation</strong> — asks a question, waits for a spoken reply
                                <em className="sm-soon"> (coming soon)</em>
                            </span>
                        </label>
                    </div>
                    <textarea
                        className="sm-textarea"
                        maxLength={4000}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="e.g. Welcome back — here's what's new in this release..."
                    />
                    <div className="sm-row">
                        <span className="sm-char-count">{text.length} / 4000</span>
                        <button className="sm-push-btn" disabled={pushing} onClick={handlePush}>
                            {pushing ? 'Pushing…' : 'Push'}
                        </button>
                    </div>
                    {statusMsg && (
                        <div className={`sm-status ${statusMsg.type === 'ok' ? 'sm-status-ok' : 'sm-status-err'}`}>
                            {statusMsg.text}
                        </div>
                    )}
                </div>

                <div className="sm-panel">
                    <h2>Release history</h2>
                    <p className="sm-hint">
                        Most recent first. The current "Latest" is what glasses play on startup.
                        Disabling a release retracts it without deleting it. Editing corrects the
                        text/type in place without re-sending it to devices. Deleting removes it
                        permanently.
                    </p>
                    {loadingHistory ? (
                        <div className="aws-loading">
                            <div className="aws-spinner"></div>
                            <p>Loading history…</p>
                        </div>
                    ) : history.length === 0 ? (
                        <p className="sm-empty">No messages pushed yet.</p>
                    ) : (
                        <table className="sm-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 70 }}>#</th>
                                    <th>Message</th>
                                    <th style={{ width: 130 }}>Type</th>
                                    <th style={{ width: 200 }}>Pushed</th>
                                    <th style={{ width: 90 }}></th>
                                    <th style={{ width: 110 }}>Enabled</th>
                                    <th style={{ width: 130 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((m) => {
                                    const isEditing = editingId === m.id;
                                    return (
                                        <tr key={m.id} className={!m.is_enabled ? 'sm-row-disabled' : ''}>
                                            <td className="sm-meta">#{m.id}</td>
                                            <td className="sm-text">
                                                {isEditing ? (
                                                    <div className="sm-edit-box">
                                                        <textarea
                                                            className="sm-edit-textarea"
                                                            maxLength={4000}
                                                            value={editText}
                                                            onChange={(e) => setEditText(e.target.value)}
                                                        />
                                                        <div className="sm-edit-type-row" role="radiogroup" aria-label="Delivery type">
                                                            <label className="sm-type-option sm-type-option-sm">
                                                                <input
                                                                    type="radio"
                                                                    name={`editType-${m.id}`}
                                                                    value="announcement"
                                                                    checked={editType === 'announcement'}
                                                                    onChange={() => setEditType('announcement')}
                                                                />
                                                                <span>Announcement</span>
                                                            </label>
                                                            <label className="sm-type-option sm-type-option-sm">
                                                                <input
                                                                    type="radio"
                                                                    name={`editType-${m.id}`}
                                                                    value="conversation"
                                                                    checked={editType === 'conversation'}
                                                                    onChange={() => setEditType('conversation')}
                                                                />
                                                                <span>Conversation</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    m.text
                                                )}
                                            </td>
                                            <td className="sm-meta">
                                                {isEditing ? '—' : (m.message_type === 'conversation' ? 'Conversation' : 'Announcement')}
                                            </td>
                                            <td className="sm-meta">
                                                {formatDate(m.created_at)}
                                                {m.pushed_by ? ` · ${m.pushed_by}` : ''}
                                            </td>
                                            <td>{m.is_latest && <span className="sm-badge">LATEST</span>}</td>
                                            <td>
                                                <label className="sm-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={m.is_enabled}
                                                        disabled={togglingId === m.id || isEditing}
                                                        onChange={(e) => handleToggle(m.id, e.target.checked)}
                                                    />
                                                    <span className="sm-switch-slider" />
                                                </label>
                                            </td>
                                            <td className="sm-actions">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            className="sm-action-btn sm-save-btn"
                                                            disabled={savingEdit}
                                                            onClick={() => saveEdit(m.id)}
                                                        >
                                                            {savingEdit ? 'Saving…' : 'Save'}
                                                        </button>
                                                        <button
                                                            className="sm-action-btn sm-cancel-btn"
                                                            disabled={savingEdit}
                                                            onClick={cancelEdit}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="sm-action-btn sm-edit-btn"
                                                            onClick={() => startEdit(m)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="sm-action-btn sm-delete-btn"
                                                            disabled={deletingId === m.id}
                                                            onClick={() => handleDelete(m.id)}
                                                        >
                                                            {deletingId === m.id ? 'Deleting…' : 'Delete'}
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StartupMessage;
